import { createSelector, Selector } from '@reduxjs/toolkit'
import {
  EntityType,
  CollateralBasketDetail,
  CollateralBasketUpdate,
  CollateralBasketType,
  CollateralBasketStatus,
  CollateralBasket,
  ProductSector
} from 'api'
import { GlobalState } from 'store'
import {
  createAllEntityUpdatesSelector,
  createAllEntitiesSelector,
  createEntitySelector,
  createEntityGetterSelector,
  createEntityUpdateSelector
} from './entitySelectors'
import { canTradeSector, doesBasketTypeRequiresTradingEntityPermission, isBasketTypePermittedToBePrimary } from 'model'
import { createAuthorisedUserSelector } from './connectionSelectors'
import { sortBy } from 'lodash'

export const allEnabledCollateralBasketsSelector: Selector<GlobalState, CollateralBasket[]> = createSelector(
  createAllEntitiesSelector(EntityType.COLLATERAL_BASKET),
  allCollateralBaskets => allCollateralBaskets.filter(collateral => collateral.status === CollateralBasketStatus.ACTIVE)
)

export const createCollateralBasketByIdGetterSelector: Selector<
  GlobalState,
  (basketId: string) => CollateralBasket | null
> = createSelector(createAllEntitiesSelector(EntityType.COLLATERAL_BASKET), collateralBaskets => (basketId: string) =>
  collateralBaskets.find(collateralBasket => collateralBasket.id === basketId) || null
)

export const createCollateralBasketByIsinCusipOrIdGetterSelector: Selector<
  GlobalState,
  (basketIsinCusipOrId: string) => CollateralBasket | null
> = createSelector(createAllEntitiesSelector(EntityType.COLLATERAL_BASKET), collateralBaskets => (basketIsinCusipOrId: string) =>
  collateralBaskets.find(
    collateralBasket =>
      collateralBasket.collateralBasketIsin === basketIsinCusipOrId ||
      collateralBasket.collateralBasketCusip === basketIsinCusipOrId ||
      collateralBasket.id === basketIsinCusipOrId
  ) || null
)

export const productSectorByIsinBuiGetterSelector: Selector<GlobalState, (collateralId: string) => ProductSector | null> = createSelector(
  [
    createCollateralBasketByIsinCusipOrIdGetterSelector,
    createAllEntitiesSelector(EntityType.COLLATERAL),
    createEntityGetterSelector(EntityType.PRODUCT_SECTOR)
  ],
  (getBasket, collaterals, getProductSector) => (collateralId: string) => {
    const basket = getBasket(collateralId)
    const collateral = collaterals.find(collateral => collateral.isin === collateralId || collateral.id === collateralId) || null
    const productSectorId = basket?.productSectorId || collateral?.productSectorId
    return getProductSector(productSectorId)
  }
)

function createCollateralBasketDetailsSelector(
  collateralBasketUpdatesSelector: Selector<GlobalState, CollateralBasketUpdate[]>
): Selector<GlobalState, CollateralBasketDetail[]> {
  return createSelector(
    collateralBasketUpdatesSelector,
    createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
    createEntityGetterSelector(EntityType.PRODUCT),
    (collateralBasketUpdates, getProductSector, getProduct) => {
      return collateralBasketUpdates.map(update => {
        const collateralBasket = update.entity
        const productSector = getProductSector(collateralBasket.productSectorId)
        const product = productSector ? getProduct(productSector.productId) : null
        const availableEvents = update.availableEvents
        return {
          collateralBasket,
          productSector,
          product,
          availableEvents
        }
      })
    }
  )
}

export function createCollateralBasketDetailSelector<TProps>(
  basketIdSelector: Selector<{}, string | null | undefined, [TProps]>
): Selector<GlobalState, CollateralBasketDetail | null, [TProps]> {
  return createSelector(
    createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
    createEntityGetterSelector(EntityType.PRODUCT),
    createEntityUpdateSelector(basketIdSelector, EntityType.COLLATERAL_BASKET),
    (getProductSector, getProduct, collateralBasketUpdate) => {
      if (!collateralBasketUpdate) {
        return null
      }
      const collateralBasket = collateralBasketUpdate.entity
      const productSector = getProductSector(collateralBasket.productSectorId)
      const product = productSector ? getProduct(productSector.productId) : null
      const availableEvents = collateralBasketUpdate.availableEvents
      return {
        collateralBasket,
        productSector,
        product,
        availableEvents
      }
    }
  )
}

export const collateralBasketDetailsSelector: Selector<GlobalState, CollateralBasketDetail[]> = createCollateralBasketDetailsSelector(
  createAllEntityUpdatesSelector(EntityType.COLLATERAL_BASKET)
)

export const allActiveCollateralBasketDetailsSelector: Selector<GlobalState, CollateralBasketDetail[]> = createSelector(
  collateralBasketDetailsSelector,
  collateralBasketDetails => {
    return collateralBasketDetails.filter(
      collateralBasketDetail => collateralBasketDetail.collateralBasket.status === CollateralBasketStatus.ACTIVE
    )
  }
)

export const clearableCollateralBasketsSelector = createSelector(
  createAllEntitiesSelector(EntityType.COLLATERAL_BASKET),
  createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  (allCollateralBaskets, getProductSector) =>
    allCollateralBaskets.filter(collateralBasket => {
      const productSector = getProductSector(collateralBasket.productSectorId)
      return productSector?.basketType !== CollateralBasketType.EUROCLEAR
    })
)

export const authorisedCollateralBasketUpdatesSelector: Selector<GlobalState, CollateralBasketUpdate[]> = createSelector(
  createAuthorisedUserSelector(),
  createAllEntityUpdatesSelector(EntityType.COLLATERAL_BASKET),
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  (authorisedUser, allCollateralBaskets, getTradingEntity, getProductSector) => {
    return allCollateralBaskets.filter(collateralBasketUpdate => {
      const productSector = getProductSector(collateralBasketUpdate.entity.productSectorId)
      const canUserTradeSector = canTradeSector(collateralBasketUpdate.entity.productSectorId, authorisedUser.productRights, null)

      return authorisedUser.tradingEntityDetails.some(d => {
        const tradingEntity = getTradingEntity(d.tradingEntityId)
        const canTradingEntityTradeSector =
          tradingEntity && canTradeSector(collateralBasketUpdate.entity.productSectorId, tradingEntity.productRights, null)

        return (
          productSector?.basketType &&
          canUserTradeSector &&
          canTradingEntityTradeSector &&
          isBasketTypePermittedToBePrimary(productSector.basketType) &&
          (!doesBasketTypeRequiresTradingEntityPermission(productSector.basketType) ||
            collateralBasketUpdate.entity.tradingEntityIds.includes(d.tradingEntityId))
        )
      })
    })
  }
)

export const authorisedCollateralBasketNumberOfItemsSelector: Selector<GlobalState, number> = createSelector(
  authorisedCollateralBasketUpdatesSelector,
  collateralBaskets =>
    collateralBaskets.filter(
      collateralBasket =>
        collateralBasket.entity.status !== CollateralBasketStatus.DELETED &&
        collateralBasket.entity.status !== CollateralBasketStatus.EXPIRED
    ).length
)

export const authorisedCollateralBasketDetailsSelector: Selector<
  GlobalState,
  CollateralBasketDetail[]
> = createCollateralBasketDetailsSelector(authorisedCollateralBasketUpdatesSelector)

export const authorisedAvailableCollateralBasketDetailsSelector: Selector<
  GlobalState,
  CollateralBasketDetail[]
> = createSelector(authorisedCollateralBasketDetailsSelector, authorisedCollateralBasketDetails =>
  authorisedCollateralBasketDetails.filter(
    collateralBasketDetail =>
      collateralBasketDetail.collateralBasket.status !== CollateralBasketStatus.DELETED &&
      collateralBasketDetail.collateralBasket.status !== CollateralBasketStatus.EXPIRED
  )
)

export const collateralBasketProductSectorIdsForTypeGetterSelector: Selector<
  GlobalState,
  (type: CollateralBasketType) => string[]
> = createSelector(createAllEntitiesSelector(EntityType.PRODUCT_SECTOR), allProductSectors => {
  return (collateralBasketType: CollateralBasketType) => {
    const filteredAndSortedSectors = sortBy(
      allProductSectors.filter(productSector => productSector.basketType && productSector.basketType === collateralBasketType),
      productSector => productSector.name
    )

    return filteredAndSortedSectors.map(productSector => productSector.id)
  }
})

export function createCollateralBasketProductSectorIdSelector<TProps>(
  buiSelector: Selector<{}, string | null | undefined, [TProps]>
): Selector<GlobalState, string | null | undefined, [TProps]> {
  return createSelector(createEntitySelector(buiSelector, EntityType.COLLATERAL_BASKET), collateralBasket =>
    collateralBasket ? collateralBasket.productSectorId : null
  )
}
